import { extends as _extends, objectWithoutProperties as _objectWithoutProperties, objectSpread2 as _objectSpread2 } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import classNames from 'classnames';
import React, { useMemo } from 'react';
import { useImageLoaded } from '../../../hooks/use-image-loaded.esm.js';
import createComponent from '../../../styles-system/create-component.esm.js';
import { positions, getPosition, getLayout, getDataPrefix, getSpace, getBorder, getAriaPrefix } from '../../../utils/props-utils.esm.js';
import Box, { BoxPrivate } from '../box/box.esm.js';
import Flex from '../flex/flex.esm.js';
import '../icon/index.esm.js';
import Icon from '../icon/icon.esm.js';

var _excluded = ["className", "__css"],
  _excluded2 = ["id", "fitPosition", "fit", "isFixed", "__css", "aspectRatio", "width", "src", "srcSet", "children", "missingImagePlaceHolder", "missingImageIcon", "alt", "title"];
var Component = createComponent('img', 'csg-box');
var ImageBase = /*#__PURE__*/React.forwardRef(function ImageBase(props, ref) {
  var className = props.className,
    __css = props.__css,
    rest = _objectWithoutProperties(props, _excluded);
  var componentStyles = useMemo(function () {
    return _objectSpread2({
      height: 'auto'
    }, __css);
  }, [__css]);
  return /*#__PURE__*/React.createElement(Component, _extends({
    ref: ref,
    className: classNames({
      'csg-tui-img': true
    }, className)
  }, rest, {
    __themeKey: "images",
    __css: componentStyles
  }));
});
var ImagePrivate = /*#__PURE__*/React.forwardRef(function ImagePrivate(props, ref) {
  var idProp = props.id,
    fitPosition = props.fitPosition,
    fit = props.fit,
    isFixed = props.isFixed,
    __css = props.__css,
    aspectRatio = props.aspectRatio,
    width = props.width,
    src = props.src,
    srcSet = props.srcSet,
    childrenProp = props.children,
    _props$missingImagePl = props.missingImagePlaceHolder,
    missingImagePlaceHolder = _props$missingImagePl === void 0 ? 'camera' : _props$missingImagePl,
    missingImageIcon = props.missingImageIcon,
    alt = props.alt,
    title = props.title,
    rest = _objectWithoutProperties(props, _excluded2);
  var imageBaseStyles = useMemo(function () {
    return _objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2({
      width: width,
      maxWidth: isFixed ? 'unset' : '100%'
    }, fit ? {
      objectFit: fit
    } : {}), fitPosition ? {
      objectPosition: fitPosition
    } : {}), aspectRatio ? {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%'
    } : {}), __css);
  }, [__css, aspectRatio, fit, fitPosition, isFixed, width]);

  // Use a hook instead of onError on the img element to support server-side rendering.
  var imageMeta = useImageLoaded({
    src: src,
    srcSet: srcSet
  });
  var imageFetchFailed = (src || srcSet) && imageMeta.state === 'error';
  var placeHolderIcon = /*#__PURE__*/React.createElement(Box, _extends({
    className: "csg-tui-missing-image-wrapper",
    width: width,
    height: "100%",
    relative: true
  }, _objectSpread2(_objectSpread2(_objectSpread2({}, getPosition(rest)), getLayout(rest)), positions(rest))), /*#__PURE__*/React.createElement(Flex, _extends({
    id: idProp,
    className: "csg-tui-missing-image",
    alt: alt,
    title: alt,
    justifyContent: "center",
    alignItems: "center",
    absolute: true,
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    bg: "white",
    overflow: "hidden"
  }, _objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2({}, getSpace(rest)), getBorder(rest)), getAriaPrefix(rest)), getDataPrefix(rest))), missingImageIcon ? missingImageIcon : missingImagePlaceHolder === 'camera' ? /*#__PURE__*/React.createElement(Icon, {
    icon: "dslrCamera",
    color: "neutral.light"
  }) : missingImagePlaceHolder === 'company' ? /*#__PURE__*/React.createElement(Icon, {
    icon: "corporateFare",
    size: "72px",
    color: "neutral.light"
  }) : missingImagePlaceHolder === 'shutter' ? /*#__PURE__*/React.createElement(Icon, {
    icon: "cameraShutter",
    color: "neutral.light"
  }) : missingImagePlaceHolder === 'contact' ? /*#__PURE__*/React.createElement(Icon, {
    icon: "mui-person",
    openSize: true,
    color: "neutral.light",
    pt: "lg",
    height: "100%"
  }) : null));
  var _children = null;
  if (!src || (src === null || src === void 0 ? void 0 : src.length) === 0) {
    _children = placeHolderIcon;
  } else if (!imageFetchFailed && !!imageMeta.state) {
    _children = /*#__PURE__*/React.createElement(ImageBase, _extends({
      id: idProp,
      alt: alt,
      title: title || alt,
      src: src,
      srcSet: srcSet,
      ref: ref
    }, rest, {
      __css: imageBaseStyles
    }));
  } else if (childrenProp != null) {
    _children = childrenProp;
  } else {
    _children = typeof missingImagePlaceHolder !== 'string' ? missingImagePlaceHolder : placeHolderIcon;
  }
  var boxPrivateStyles = useMemo(function () {
    return {
      width: width ? width : '100%',
      height: 0,
      paddingTop: typeof aspectRatio === 'number' ? "".concat(aspectRatio * 100, "%") : '75%'
    };
  }, [aspectRatio, width]);
  return aspectRatio ? /*#__PURE__*/React.createElement(BoxPrivate, {
    className: "csg-tui-img-placeholder-aspect-ratio",
    relative: true,
    __css: boxPrivateStyles
  }, _children) :
  /*#__PURE__*/
  // eslint-disable-next-line react/jsx-no-useless-fragment
  React.createElement(React.Fragment, null, _children);
});
var Image = /*#__PURE__*/React.forwardRef(function Image(props, ref) {
  return /*#__PURE__*/React.createElement(ImagePrivate, _extends({
    ref: ref
  }, props));
});

export default Image;
export { ImagePrivate };
//# sourceMappingURL=image.esm.js.map
